import Dexie, { type Table } from 'dexie';

export type PosSyncMode = 'Live Online' | 'Local First Auto Sync' | 'Offline Always Manual Sync';

export type SyncQueueType =
  | 'sale'
  | 'stock_in'
  | 'stock_adjust'
  | 'stock_out'
  | 'create_item'
  | 'update_item'
  | 'create_customer'
  | 'update_customer'
  | 'create_supplier'
  | 'update_supplier'
  | 'purchase_invoice'
  | 'purchase_payment'
  | 'expense'
  | 'sales_return';

export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

export interface MetaRow {
  key: string;
  value: unknown;
}

export interface CatalogItem {
  name: string;
  item_code?: string;
  item_name: string;
  image?: string;
  category?: string;
  brand?: string;
  custom_model?: string;
  item_type: 'Product' | 'Service';
  sale_price: number;
  wholesale_price?: number;
  reseller_price?: number;
  minimum_price?: number;
  cost_price?: number;
  tax_rate?: number;
  stock_quantity?: number;
  barcode?: string;
  unit_of_measurement?: string;
  has_variants?: boolean;
  is_active?: boolean;
  allow_fractional_qty?: number;
  maintain_stock?: boolean;
  custom_fields_data?: Record<string, string>;
  variants?: Array<Record<string, unknown>>;
  isLocal?: boolean;
}

export interface CatalogCustomer {
  name: string;
  customer_name: string;
  mobile?: string | null;
  email?: string | null;
  city?: string | null;
  address?: string | null;
  is_walk_in?: number;
  outstanding_balance?: number;
  is_default?: number;
  customer_type?: string;
  creation?: string;
  isLocal?: boolean;
}

export interface CatalogSupplier {
  name: string;
  supplier_name: string;
  mobile?: string | null;
  email?: string | null;
  city?: string | null;
  address?: string | null;
  isLocal?: boolean;
}

export interface LocalStockRow {
  id: string; // item|variant
  item: string;
  variant?: string;
  qty: number;
}

export interface LocalStockMovement {
  clientOfflineId: string;
  type: 'stock_in' | 'stock_adjust' | 'stock_out';
  item: string;
  variant?: string;
  quantity: number;
  unit_price?: number;
  reason?: string;
  createdAt: string;
  serverName?: string;
}

export interface LocalSale {
  clientOfflineId: string;
  offlineInvoiceNo: string;
  createdAt: string;
  customer: string;
  customerName?: string;
  items: Array<Record<string, unknown>>;
  discount_amount: number;
  promo_total?: number;
  promo_campaigns?: string;
  tax_amount: number;
  write_off_amount?: number;
  payments: Array<Record<string, unknown>>;
  notes?: string;
  net_total: number;
  paid_amount: number;
  status: string;
  serverInvoiceName?: string;
  offlineSource?: string;
}

export interface LocalPurchase {
  clientOfflineId: string;
  createdAt: string;
  supplier: string;
  supplierName?: string;
  items: Array<Record<string, unknown>>;
  discount_amount?: number;
  tax_amount?: number;
  net_total: number;
  paid_amount: number;
  notes?: string;
  serverName?: string;
}

export interface LocalExpense {
  clientOfflineId: string;
  createdAt: string;
  amount: number;
  category?: string;
  payment_method?: string;
  cash_pocket?: string;
  bank_account?: string;
  remarks?: string;
  serverName?: string;
  recorded_by?: string;
  recorded_by_name?: string;
  status?: string;
  /** True when row came from catalog sync (not a local unsynced create). */
  seededFromServer?: boolean;
}

export interface LocalReturn {
  clientOfflineId: string;
  createdAt: string;
  pos_invoice: string;
  items: Array<Record<string, unknown>>;
  refund_amount: number;
  serverName?: string;
}

export interface SyncQueueItem {
  clientOfflineId: string;
  type: SyncQueueType;
  status: SyncStatus;
  payload: Record<string, unknown>;
  offlineSource?: string;
  createdAt: string;
  retryCount: number;
  serverName?: string;
  error?: string;
  priority: number; // lower = earlier
}

export class OfflineDB extends Dexie {
  meta!: Table<MetaRow, string>;
  catalog_items!: Table<CatalogItem, string>;
  catalog_customers!: Table<CatalogCustomer, string>;
  catalog_suppliers!: Table<CatalogSupplier, string>;
  catalog_settings!: Table<MetaRow, string>;
  local_stock!: Table<LocalStockRow, string>;
  local_stock_movements!: Table<LocalStockMovement, string>;
  local_sales!: Table<LocalSale, string>;
  local_purchases!: Table<LocalPurchase, string>;
  local_expenses!: Table<LocalExpense, string>;
  local_returns!: Table<LocalReturn, string>;
  sync_queue!: Table<SyncQueueItem, string>;

  constructor(companyCode: string) {
    super(`saledesk-pos-${companyCode}`);
    this.version(1).stores({
      meta: 'key',
      catalog_items: 'name, item_type, category, barcode',
      catalog_customers: 'name, customer_name, mobile',
      catalog_suppliers: 'name, supplier_name, mobile',
      catalog_settings: 'key',
      local_stock: 'id, item',
      local_stock_movements: 'clientOfflineId, item, type, createdAt',
      local_sales: 'clientOfflineId, createdAt, customer, status',
      local_purchases: 'clientOfflineId, createdAt, supplier',
      local_expenses: 'clientOfflineId, createdAt',
      local_returns: 'clientOfflineId, createdAt, pos_invoice',
      sync_queue: 'clientOfflineId, status, type, priority, createdAt',
    });
  }
}

const dbCache = new Map<string, OfflineDB>();

export function getOfflineDb(companyCode: string): OfflineDB {
  const key = companyCode || 'default';
  let db = dbCache.get(key);
  if (!db) {
    db = new OfflineDB(key);
    dbCache.set(key, db);
  }
  return db;
}

export function stockKey(item: string, variant?: string) {
  return `${item}|${variant || ''}`;
}

export function getOrCreateDeviceId(): string {
  const key = 'pos_device_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

export const SYNC_CHUNK_SIZE = 25;

export const QUEUE_PRIORITY: Record<SyncQueueType, number> = {
  create_item: 10,
  update_item: 11,
  create_customer: 20,
  update_customer: 21,
  create_supplier: 30,
  update_supplier: 31,
  stock_in: 40,
  stock_adjust: 41,
  stock_out: 42,
  sale: 50,
  purchase_invoice: 51,
  purchase_payment: 52,
  expense: 53,
  sales_return: 54,
};
